import { useOrgAndSiteContext } from "@/context";
import { IOrganization } from "@/types/organization";
import { langueges, setLanguage } from "@/utils/i18n";
import useClickOutside from "@shared/common/hooks/use-click-outside";
import {
  BookText,
  Check,
  ChevronDown,
  ChevronUp,
  FileCode2,
  Globe,
  Megaphone,
  NotepadText,
  PanelsTopLeft,
  PieChart,
  Puzzle,
  ReceiptText,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { Fragment, ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { twMerge } from "tailwind-merge";
import V2Tooltip from "../common/v2-tooltip";
import styles from "./layout.module.css";
import SitesDropdown from "./sites-dropdown";

interface ISidebar {
  collapsed?: boolean;
  currentOrganization?: IOrganization;
}

interface IMenuItem {
  key: string;
  type: "menu" | "divider";
  gtmId: string;
  link?: string;
  linkDepth?: "root" | "org" | "site";
  label?: string;
  icon?: React.ReactNode;
  badge?: number;
  subMenu?: IMenuItem[];
  color?: "primary";
  disabled?: boolean;
  outLink?: boolean;
  activeOnlyExact?: boolean;
  subLabel?: string;
  onlyAdmin?: boolean;
}

// 메뉴 key는 변경하지 마세요! (i18n key와 연결되어 있습니다.)
// const aiMenuItems: IMenuItem[] = [
//   {
//     key: "ai.copywriting",
//     type: "menu",
//     label: "카피라이팅",
//     link: "ai/copywriting",
//     linkDepth: "org",
//     icon: (
//       <img src="/v2-icon/copywriting.svg" alt="copywriting" className="icon" />
//     ),
//   },
//   {
//     key: "ai.toneAndManner",
//     type: "menu",
//     label: "톤 앤 매너",
//     link: "ai/tone",
//     linkDepth: "org",
//     icon: <img src="/v2-icon/tone.svg" alt="tone" className="icon" />,
//   },
//   {
//     key: "ai.removeBackground",
//     type: "menu",
//     label: "배경지우기",
//     link: "ai/remove-bg",
//     linkDepth: "org",
//     icon: <img src="/v2-icon/removebg.svg" alt="removebg" className="icon" />,
//   },
// ];

const campaignMenuItems: IMenuItem[] = [
  {
    key: "popup",
    type: "menu",
    label: "팝업",
    link: "popup",
    linkDepth: "site",
    gtmId: "menu_popup",
  },
  {
    key: "groupPopup",
    type: "menu",
    label: "그룹 팝업",
    link: "popup-group",
    linkDepth: "site",
    gtmId: "menu_gpopup",
  },
  {
    key: "banner",
    type: "menu",
    label: "배너",
    link: "banner",
    linkDepth: "site",
    gtmId: "menu_banner",
  },
  {
    key: "instagram",
    type: "menu",
    label: "인스타그램",
    link: "instagram",
    linkDepth: "site",
    gtmId: "menu_insta",
  },
];

const pageMenuItems: IMenuItem[] = [
  {
    key: "eventPage",
    type: "menu",
    label: "이벤트 페이지",
    link: "promotion",
    linkDepth: "site",
    gtmId: "menu_eventpage",
  },
];

const formMenuItems: IMenuItem[] = [
  {
    key: "form.eventForm",
    type: "menu",
    label: "이벤트",
    link: "form-event",
    linkDepth: "site",
    gtmId: "menu_eventform",
  },
  {
    key: "form.survey",
    type: "menu",
    label: "설문조사",
    link: "form-survey",
    linkDepth: "site",
    gtmId: "menu_survey",
  },
];

const menuItems: IMenuItem[] = process.env.NEXT_PUBLIC_ENABLE_EMBED
  ? [
      {
        key: "popup",
        type: "menu",
        label: "팝업",
        badge: 0,
        icon: <Megaphone className="w-4 h-4" />,
        link: "popup",
        linkDepth: "site",
        gtmId: "menu_popup",
      },
    ]
  : [
      {
        key: "dashboard",
        type: "menu",
        label: "대시보드",
        link: "",
        linkDepth: "site",
        icon: <PanelsTopLeft className="w-4 h-4" />,
        activeOnlyExact: true,
        gtmId: "menu_dashboard",
      },
      {
        key: "divider-main",
        type: "divider",
        gtmId: "menu_divider",
      },
      {
        key: "campaign",
        type: "menu",
        label: "캠페인",
        badge: 0,
        icon: <Megaphone className="w-4 h-4" />,
        link: "",
        linkDepth: "site",
        subMenu: campaignMenuItems,
        gtmId: "menu_campaign",
      },
      {
        key: "page",
        type: "menu",
        label: "페이지",
        badge: 0,
        icon: <img src="/v2-icon/lnb-page.svg" alt="page" className="icon" />,
        link: "",
        linkDepth: "site",
        subMenu: pageMenuItems,
        gtmId: "menu_page",
      },
      {
        key: "form",
        type: "menu",
        label: "폼",
        icon: <NotepadText className="w-4 h-4" />,
        link: "",
        linkDepth: "site",
        subMenu: formMenuItems,
        gtmId: "menu_form",
      },
      {
        key: "divider-ai-up",
        type: "divider",
        gtmId: "menu_divider",
      },
      {
        key: "ai",
        type: "menu",
        label: "AI 허브",
        icon: <img src="/v2-icon/lnb-ai2.svg" alt="ai" className="icon" />,
        link: "ai",
        linkDepth: "org",
        gtmId: "menu_ai",
      },
      {
        key: "divider-ai-down",
        type: "divider",
        gtmId: "menu_divider",
      },
      {
        key: "stat",
        type: "menu",
        label: "통계",
        icon: <PieChart className="w-4 h-4" />,
        link: "stats",
        linkDepth: "site",
        gtmId: "menu_statistics",
      },
      {
        key: "plan",
        type: "menu",
        label: "플랜 안내",
        icon: <img src="/v2-icon/lnb-plan.svg" alt="plan" className="icon" />,
        link: "payment/plan",
        linkDepth: "org",
        gtmId: "menu_plan",
      },
      {
        key: "apps",
        type: "menu",
        label: "앱 스토어",
        icon: <img src="/v2-icon/apps.svg" alt="apps" className="icon" />,
        link: "apps",
        linkDepth: "site",
        gtmId: "apps",
      },
    ];

const guideMenuItems: IMenuItem[] = process.env.NEXT_PUBLIC_ENABLE_EMBED
  ? []
  : [
      {
        key: "dev",
        type: "menu",
        label: "디자인 시스템",
        link: "dev",
        linkDepth: "root",
        icon: <Puzzle className="w-4 h-4" />,
        onlyAdmin: true,
        gtmId: "menu_dev",
      },
      {
        key: "recipe",
        type: "menu",
        label: "마케팅 레시피",
        link: `${process.env.NEXT_PUBLIC_BLOG_URL}/category/insight/recipe/`,
        icon: <ReceiptText className="w-4 h-4" />,
        outLink: true,
        gtmId: "menu_recipe",
      },
      {
        key: "guide",
        type: "menu",
        label: "사용자 가이드",
        link: `${process.env.NEXT_PUBLIC_BLOG_URL}/guide/`,
        icon: <BookText className="w-4 h-4" />,
        outLink: true,
        gtmId: "menu_guide",
      },
      {
        key: "docs",
        type: "menu",
        label: "개발자 가이드",
        link: `https://docs.codenbutter.com/`,
        icon: <FileCode2 className="w-4 h-4" />,
        outLink: true,
        gtmId: "menu_docs",
      },
    ];

const v2UrlPrefix = process.env.NEXT_PUBLIC_V2_URL_PREFIX;

const isSubPath = (path: string, currentPath: string) => {
  const pathSegments = path.split("/").filter(Boolean);
  const currentPathSegments = currentPath.split("/").filter(Boolean);

  return pathSegments.every(
    (segment, index) => currentPathSegments[index] === segment
  );
};

const Portal = ({ children }: { children: ReactNode }) => {
  const sidebarElement = document.getElementById("cb-sidebar");
  return createPortal(children, sidebarElement || document.body);
};

const Sidebar: React.FC<ISidebar> = ({ collapsed, currentOrganization }) => {
  const { t } = useTranslation("lnb");
  const router = useRouter();

  const nodeRef = useRef(null);

  const { currentSite, currentOrFallbackSites: sites } = useOrgAndSiteContext();

  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [collapsedMenu, setCollapsedMenu] = useState<{
    [key: string]: boolean;
  }>({});

  const { setDom: setDropdownRef } = useClickOutside(() =>
    setShowLanguageDropdown(false)
  );

  const isAdmin =
    process.env.NODE_ENV === "development" ||
    currentOrganization?.slug === "codenbutter";

  const renderMenuItem: (
    menu: IMenuItem[],
    isSubmenu?: boolean
  ) => JSX.Element[] = (menu: IMenuItem[], isSubmenu = false) => {
    return menu.map((item: IMenuItem) => {
      const pathname = (
        item.linkDepth
          ? item.linkDepth === "root"
            ? `${v2UrlPrefix}/${item.link}`
            : item.linkDepth === "org" && currentOrganization
            ? `${v2UrlPrefix}/[org_slug]/${item.link}`
            : item.linkDepth === "site" && currentOrganization
            ? `${v2UrlPrefix}/[org_slug]/[site_public_id]/${item.link}`
            : "#"
          : "#"
      ).replace(/\/$/, "");

      const query = item.linkDepth
        ? item.linkDepth === "root"
          ? {}
          : item.linkDepth === "org" && currentOrganization
          ? { org_slug: currentOrganization.slug }
          : item.linkDepth === "site" && currentOrganization
          ? {
              org_slug: currentOrganization.slug,
              site_public_id: currentSite?.publicId,
            }
          : {}
        : {};

      const menuDisabled =
        (item.key === "dashboard" && (!sites || sites.length) === 0) ||
        item.disabled;

      const getIsCurrentMenu = (pathname: string) => {
        return isSubPath(pathname, router.pathname);
      };

      const menuWithIcon = (isCurrentMenu = false) => {
        return (
          <div
            key={item.key}
            className={twMerge(
              "relative menu-v2 cursor-pointer hover:bg-cb-gray-200 hover:text-cb-gray-900",
              isSubmenu && !collapsed && "ml-6",
              collapsed && "w-[32px] px-[3px]",
              isCurrentMenu &&
                !item.subMenu &&
                "bg-cb-gray-200 hover:bg-cb-gray-200",
              menuDisabled &&
                "hover:bg-cb-gray-100 opacity-20 hover:bg-opacity-100 cursor-default hover:text-cb-gray-900"
            )}
          >
            {item.icon}
            <span
              className={twMerge(
                "flex items-center flex-1",
                !isSubmenu && "ml-2",
                collapsed && "opacity-0"
              )}
            >
              <span>{t(`menu.${item.key}.label`)}</span>
              {item.subLabel ? (
                <span className="px-1 ml-2 text-xs text-white rounded bg-chartPurple-200">
                  {t(`menu.${item.key}.subLabel`)}
                </span>
              ) : (
                ""
              )}
            </span>
            {/* {item.badge && (
              <span className="inline-flex items-center justify-center w-3 h-3 p-3 ml-3 text-sm font-bold rounded-full text-primary-700 bg-secondary-400">
                {item.badge}
              </span>
            )}
            {item.badge && collapsed && (
              <div className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full text-primary-700 bg-secondary-400 top-0.5 right-0.5">
                {item.badge}
              </div>
            )} */}
            {item.subMenu ? (
              collapsedMenu[item.key] ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )
            ) : (
              <></>
            )}
          </div>
        );
      };
      const renderMenuComponent = () => (
        <V2Tooltip
          placement="right"
          title={collapsed && t(`menu.${item.key}.label`)}
          key={item.key}
        >
          <li key={item.key} className="transition-all">
            {pathname === "#" ? (
              item.outLink ? (
                // 네비게이션 메뉴의 경우 내부 서비스 링크일거라 rel 처리 안함
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  href={item.link}
                  target="_blank"
                  className="hover:text-cb-gray-900"
                  data-gtm-id={item.gtmId}
                >
                  {menuWithIcon()}
                </a>
              ) : (
                menuWithIcon()
              )
            ) : menuDisabled ? (
              menuWithIcon(
                item.activeOnlyExact
                  ? router.pathname === pathname
                  : getIsCurrentMenu(pathname)
              )
            ) : item.subMenu ? (
              <div
                onClick={() =>
                  setCollapsedMenu({
                    ...collapsedMenu,
                    [item.key]: !collapsedMenu[item.key],
                  })
                }
              >
                {menuWithIcon()}
              </div>
            ) : (
              <Link
                href={{
                  pathname: pathname === "#" ? "" : pathname,
                  query,
                }}
                data-gtm-id={item.gtmId}
                className="hover:text-cb-gray-900"
              >
                {menuWithIcon(
                  item.activeOnlyExact
                    ? router.pathname === pathname
                    : getIsCurrentMenu(pathname)
                )}
              </Link>
            )}
          </li>
        </V2Tooltip>
      );

      return item.type === "menu" ? (
        <Fragment key={item.key}>
          {item.onlyAdmin ? (
            isAdmin ? (
              renderMenuComponent()
            ) : (
              <></>
            )
          ) : (
            renderMenuComponent()
          )}
          {item.subMenu && (
            <>
              {collapsed && (
                <li key={item.key} className="w-full mt-[3px]">
                  <hr className="absolute left-0 right-0 w-[36px] box-border border border-white rounded mx-auto" />
                </li>
              )}
              {!collapsedMenu[item.key] && renderMenuItem(item.subMenu, true)}
            </>
          )}
        </Fragment>
      ) : item.type === "divider" ? (
        <li key={item.key} className="border-b border-cb-gray-200" />
      ) : (
        <></>
      );
    });
  };

  return (
    <aside
      id="cb-sidebar"
      aria-label="Sidebar"
      className={`flex-none sticky top-0 translate-x-0 bg-cb-gray-50 text-cb-gray-900 h-screen z-50 ${
        collapsed ? "w-[80px]" : "w-56"
      }`}
    >
      <div className="h-screen">
        <div className="px-3 py-4">
          <SitesDropdown />
        </div>
        <div
          className="overflow-y-auto"
          style={{ height: "calc(100vh - var(--header-height))" }}
        >
          <div className="flex flex-col justify-between min-h-full gap-1 pb-3 pl-3 pr-3">
            <div className="flex flex-col gap-1">
              <ul className="flex flex-col gap-1">
                {renderMenuItem(menuItems)}
              </ul>
              {currentSite?.siteType === "CAFE24" ? (
                <a
                  href="https://form.buttr.site/form-event?s=rzjfnulhvi&c=zwhswjskgqch"
                  target="_blank"
                >
                  <img
                    src="/images/v2/cafe24_allinone2.png"
                    alt="카페24 올인원패키지 안내"
                  />
                </a>
              ) : (
                <></>
              )}
            </div>
            <ul>
              {renderMenuItem(guideMenuItems)}

              <li className="my-4 border-b border-cb-gray-200" />
              <li className="flex items-center justify-between">
                <Link href="/v2" legacyBehavior>
                  <a className="block leading-none" data-gtm-id="logo">
                    <img
                      src="/logo/lnb-logo.png"
                      alt="logo"
                      width="86"
                      height="19"
                    />
                  </a>
                </Link>
                <div>
                  <button
                    className="text-sm flex items-center gap-1 px-2 py-1.5 rounded-md text-cb-gray-500 hover:bg-cb-gray-200 hover:text-cb-gray-900"
                    onClick={() => setShowLanguageDropdown(true)}
                  >
                    <Globe className="w-3.5" /> {router.locale}
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Portal>
        <CSSTransition
          nodeRef={nodeRef}
          in={showLanguageDropdown}
          classNames="popup-transition"
          timeout={200}
          unmountOnExit
        >
          <div ref={nodeRef} className={styles["sidebar-lang-dropdown-option"]}>
            <div ref={setDropdownRef}>
              <ul className={styles["sidebar-sites-dropdown-option-list"]}>
                {langueges.map((lang) => (
                  <li key={lang.value}>
                    <button
                      onClick={() => {
                        setShowLanguageDropdown(false);
                        setLanguage(lang.value, router);
                      }}
                      className="flex w-full gap-1 px-2 py-1 center-center hover:bg-cb-gray-200"
                    >
                      {lang.beta ? (
                        <span
                          className={
                            lang.value === router.locale ? "w-1" : "w-5"
                          }
                        ></span>
                      ) : (
                        <></>
                      )}
                      {lang.value === router.locale ? (
                        <span>
                          <Check className="w-4" />
                        </span>
                      ) : (
                        <></>
                      )}
                      <span>
                        {lang.label}
                        {lang.beta ? (
                          <sup className="ml-1 text-right text-cb-gray-600">
                            beta
                          </sup>
                        ) : (
                          <></>
                        )}
                      </span>
                      {lang.value === router.locale ? (
                        <span className={lang.beta ? "" : "w-4"}></span>
                      ) : (
                        <></>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CSSTransition>
      </Portal>
    </aside>
  );
};

export default Sidebar;
